import Vue from 'vue';
import {
    CombinationsChecklistFieldConfig,
    CombinationsConfigurationItem,
    combinationsConfiguration,
    resolveCanvasTemplateConfiguration,
} from '@/modules/ConfiguratorCombinations/helpers/CombinationsConfiguration';
import { MultipositionProduct } from '@/interfaces/components/projectNew/MultipositionProduct';
import { ChecklistFieldEntry } from '@/interfaces/components/configurator/ChecklistFieldEntry';
import { ConfiguratorValue } from '@/interfaces/components/configurator/ConfiguratorValue';
import { ProductRowItem } from '@/interfaces/general/ProductRowItem';
import { CombinationsConfiguratorFormData } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';
import { getCombinedWidth, getWidthDifference } from '@/modules/ConfiguratorCombinations/helpers/CombinationsHelper';
import { GuideType, IndividualGuideTypes } from '@/modules/ConfiguratorCombinations/composables/useGuideImages';
import {
    CombinationCircleLetter,
    CombinationsCanvasRenderProps,
    renderCombinationsCanvas,
} from '@/modules/ConfiguratorCombinations/helpers/CombinationsCanvasRenderer';
import {
    getNextCombinationConnection,
    syncCombinationConnectionCounter,
} from '@/modules/ConfiguratorCombinations/helpers/CombinationsConnectionAllocator';

type CanvasItemPayload = MultipositionProduct & {
    formData: ChecklistFieldEntry[];
};

interface MappedConnectionItem {
    payload: MultipositionProduct;
    row: ProductRowItem;
    currentConfig: CombinationsConfigurationItem;
}

type MappedConnectionItems = Record<number, MappedConnectionItem[]>;

interface FormattedChecklistItem {
    canvasFormData: Record<string, ConfiguratorValue>;
    offerItemId: string | null;
}

interface FormattedConnectionGroup {
    currentConfig: CombinationsConfigurationItem;
    items: FormattedChecklistItem[];
}

type FormattedChecklistValues = Record<number, FormattedConnectionGroup>;

export type CombinationCanvasImages = Record<number, { image: string; type: 'sestav' }>;

function getConfigurationPid(currentConfig: CombinationsConfigurationItem, fieldName: string): string {
    const field = currentConfig.checklistFields[fieldName];

    return field ? String(field.pid) : '';
}

function getCanvasValue(item: FormattedChecklistItem, pid: string): ConfiguratorValue | undefined {
    return pid ? item.canvasFormData[pid] : undefined;
}

function getStringCanvasValue(item: FormattedChecklistItem, pid: string): string {
    const value = getCanvasValue(item, pid);

    return value === undefined || value === 'Nedefinirano' || value === null ? '' : String(value);
}

function getBooleanCanvasValue(item: FormattedChecklistItem, pid: string): boolean {
    const value = getCanvasValue(item, pid);

    return value === true || value === 1 || value === '1' || value === 'true';
}

function sortItemsByAssemblyPosition(
    items: FormattedChecklistItem[],
    currentConfig: CombinationsConfigurationItem,
): FormattedChecklistItem[] {
    const positionPid = getConfigurationPid(currentConfig, 'ASSEMBLY_POSITION_PID');

    return [...items].sort((firstItem, secondItem) => {
        const firstPosition = Number(getCanvasValue(firstItem, positionPid));
        const secondPosition = Number(getCanvasValue(secondItem, positionPid));

        if (!Number.isFinite(firstPosition) || !Number.isFinite(secondPosition)) {
            return 0;
        }

        return firstPosition - secondPosition;
    });
}

function getFieldValue(item: FormattedChecklistItem, field: CombinationsChecklistFieldConfig): ConfiguratorValue {
    const pid = String(field.pid);
    const savedValue = getCanvasValue(item, pid);

    if (savedValue !== undefined) {
        return savedValue;
    }

    if (field.value !== undefined) {
        return field.value;
    }

    if (field.defaultValue !== undefined) {
        return field.defaultValue;
    }

    return '';
}

function createConfiguratorRows(
    items: FormattedChecklistItem[],
    currentConfig: CombinationsConfigurationItem,
): CombinationsConfiguratorFormData[] {
    const rows: CombinationsConfiguratorFormData[] = [];
    const checklistFields = Object.values(currentConfig.checklistFields);

    for (let index = 0; index < items.length; index++) {
        const item = items[index];
        const formData: CombinationsConfiguratorFormData['formData'] = [];

        for (const field of checklistFields) {
            formData.push({
                fieldPID: String(field.pid),
                fieldLabel: field.label,
                fieldValue: getFieldValue(item, field),
                shouldUpdateConfigurator: field.shouldUpdateConfigurator,
                isReadOnly: field.isReadOnly,
                segment: field.segment,
                options: field.options,
                fieldType: field.type,
                step: field.step,
                isNarrow: field.isNarrow,
                key: field.key,
                defaultValue: field.defaultValue === undefined ? undefined : String(field.defaultValue),
            });
        }

        rows.push({
            activeProductFormId: index + 1,
            isHighlighted: true,
            formData,
        });
    }

    return rows;
}

function findFieldByKey(
    currentConfig: CombinationsConfigurationItem,
    key: string,
): CombinationsChecklistFieldConfig | undefined {
    return Object.values(currentConfig.checklistFields).find((field) => field.key === key);
}

function mapSavedGuideType(value: ConfiguratorValue | undefined, guideField?: CombinationsChecklistFieldConfig) {
    if (value === undefined || !guideField?.options) {
        return null;
    }

    const option = guideField.options.find((guideOption) => guideOption.value === String(value));
    const guideType = option?.label;

    if (guideType === 'wireGuide' || guideType === 'inoxGuide' || guideType === 'aluGuide') {
        return guideType as GuideType;
    }

    return null;
}

function createIndividualGuideTypes(
    items: FormattedChecklistItem[],
    currentConfig: CombinationsConfigurationItem,
): IndividualGuideTypes {
    const individualGuideTypes: IndividualGuideTypes = {
        1: { leftCorner: 'aluGuide', rightCorner: 'aluGuide' },
        2: { leftCorner: 'aluGuide', rightCorner: 'aluGuide' },
        3: { leftCorner: 'aluGuide', rightCorner: 'aluGuide' },
    };
    const guideField = findFieldByKey(currentConfig, 'guideType');
    const leftGuideField = findFieldByKey(currentConfig, 'leftGuide');
    const rightGuideField = findFieldByKey(currentConfig, 'rightGuide');

    if (!guideField) {
        return individualGuideTypes;
    }

    for (let index = 0; index < items.length && index < 3; index++) {
        const item = items[index];
        const windowIndex = (index + 1) as keyof IndividualGuideTypes;
        const commonGuideValue = getCanvasValue(item, String(guideField.pid));
        const commonGuideType = mapSavedGuideType(commonGuideValue, guideField);

        if (commonGuideType) {
            individualGuideTypes[windowIndex].leftCorner = commonGuideType;
            individualGuideTypes[windowIndex].rightCorner = commonGuideType;
            continue;
        }

        if (leftGuideField) {
            const leftGuideType = mapSavedGuideType(getCanvasValue(item, String(leftGuideField.pid)), guideField);

            if (leftGuideType) {
                individualGuideTypes[windowIndex].leftCorner = leftGuideType;
            }
        }

        if (rightGuideField) {
            const rightGuideType = mapSavedGuideType(getCanvasValue(item, String(rightGuideField.pid)), guideField);

            if (rightGuideType) {
                individualGuideTypes[windowIndex].rightCorner = rightGuideType;
            }
        }
    }

    return individualGuideTypes;
}

function shouldUseAlternativeCenterGuide(
    items: FormattedChecklistItem[],
    currentConfig: CombinationsConfigurationItem,
): boolean {
    if (currentConfig.configuratorType === 'hero-2') {
        return true;
    }

    if (currentConfig.configuratorType !== 'hero-1') {
        return false;
    }

    const floorMountingPid = getConfigurationPid(currentConfig, 'FLOOR_MOUNTING_HERO_PID');

    return !items.every((item) => getStringCanvasValue(item, floorMountingPid) === 'Brez');
}

function getTotalWidth(
    items: FormattedChecklistItem[],
    currentConfig: CombinationsConfigurationItem,
    combinedWidth: number,
): number {
    const totalWidthPid = getConfigurationPid(currentConfig, 'TOTAL_WIDTH_PID');

    for (const item of items) {
        const totalWidth = Number(getCanvasValue(item, totalWidthPid));

        if (Number.isFinite(totalWidth) && totalWidth > 0) {
            return totalWidth;
        }
    }

    return combinedWidth;
}

function getCircleLetters(
    items: FormattedChecklistItem[],
    currentConfig: CombinationsConfigurationItem,
): CombinationCircleLetter[][] {
    const drivePid = getConfigurationPid(currentConfig, 'DRIVE_PID');
    const circleLetters: CombinationCircleLetter[][] = [];

    for (const item of items) {
        const drive = getStringCanvasValue(item, drivePid);

        circleLetters.push([{ letter: drive, visible: !!drive }]);
    }

    return circleLetters;
}

function createCanvasRenderProps(group: FormattedConnectionGroup): CombinationsCanvasRenderProps {
    const { currentConfig } = group;
    const items = sortItemsByAssemblyPosition(group.items, currentConfig);
    const productFormData = createConfiguratorRows(items, currentConfig);
    const selectedRows = productFormData.map((row) => row.activeProductFormId);
    const widthPid = getConfigurationPid(currentConfig, 'WIDTH_PID');
    const finalWidthPid = getConfigurationPid(currentConfig, 'FINAL_WIDTH_PID');
    const dimensionsTypePid = getConfigurationPid(currentConfig, 'DIMENSIONS_TYPE_PID');
    const combinedWidth = getCombinedWidth({
        rows: productFormData,
        selectedIds: selectedRows,
        widthPID: widthPid,
        finalWidthPID: finalWidthPid,
        dimensionsTypePID: dimensionsTypePid,
    });
    const totalWidth = getTotalWidth(items, currentConfig, combinedWidth);
    const rightGuidePid = getConfigurationPid(currentConfig, 'RIGHT_GUIDE_PID');
    const combinationTypePid = getConfigurationPid(currentConfig, 'COMBINATION_TYPE_PID');
    const combinedAssemblyPid = getConfigurationPid(currentConfig, 'COMBINED_ASSEMBLY_PID');
    const useAlternativeCenterGuide = shouldUseAlternativeCenterGuide(items, currentConfig);
    const hasGuideStep = currentConfig.configuratorSteps >= 4;

    return {
        productFormData,
        selectedRows,
        guideType: hasGuideStep ? getStringCanvasValue(items[0], rightGuidePid) : '',
        secondGuideType: hasGuideStep && items.length === 3 ? getStringCanvasValue(items[1], rightGuidePid) : '',
        combinationType: getStringCanvasValue(items[0], combinationTypePid),
        isLastStep: true,
        widthDifference: getWidthDifference(totalWidth, combinedWidth),
        combinedWidth,
        currentStep: currentConfig.configuratorSteps,
        currentConfig,
        canvasTemplateConfiguration: resolveCanvasTemplateConfiguration(currentConfig, useAlternativeCenterGuide),
        configuratorType: currentConfig.configuratorType,
        disableDownloadButton: true,
        useSeparateBoxes:
            currentConfig.showSeparatedWindowsOptions && getBooleanCanvasValue(items[0], combinedAssemblyPid),
        individualGuideTypes: createIndividualGuideTypes(items, currentConfig),
        circleLetters: getCircleLetters(items, currentConfig),
    };
}

export async function buildCanvasFromChecklistValues(
    checklistValues: FormattedChecklistValues,
): Promise<CombinationCanvasImages> {
    const canvases: CombinationCanvasImages = {};

    for (const [connection, group] of Object.entries(checklistValues)) {
        if (!group.items.length) {
            continue;
        }

        const image = await renderCombinationsCanvas(createCanvasRenderProps(group));

        if (image) {
            canvases[Number(connection)] = { image, type: 'sestav' };
        }
    }

    return canvases;
}

export function extractAndFormatChecklistValues(
    mappedConnectionItems: MappedConnectionItems,
): FormattedChecklistValues {
    const formattedValues: FormattedChecklistValues = {};

    for (const [connection, connectionItems] of Object.entries(mappedConnectionItems)) {
        const firstConnectionItem = connectionItems[0];

        if (!firstConnectionItem) {
            continue;
        }

        const formattedItems: FormattedChecklistItem[] = [];

        for (const { payload, row, currentConfig } of connectionItems) {
            const canvasFormData: Record<string, ConfiguratorValue> = {};
            const formData = (payload as CanvasItemPayload).formData;

            for (const checklistField of Object.values(currentConfig.checklistFields)) {
                const pid = String(checklistField.pid);
                const formField = formData.find((field) => field.id === pid);

                if (formField) {
                    canvasFormData[pid] = formField.value;
                }
            }

            formattedItems.push({
                canvasFormData,
                offerItemId: row.offerItem?.id ?? null,
            });
        }

        formattedValues[Number(connection)] = {
            currentConfig: firstConnectionItem.currentConfig,
            items: formattedItems,
        };
    }

    return formattedValues;
}

function mapProductTypeToConfiguration(
    itemRow: ProductRowItem,
    productSystemName?: string,
): CombinationsConfigurationItem | undefined {
    const productSystem = itemRow.offerItem?.name || productSystemName || itemRow.productName || '';

    return combinationsConfiguration.find((config) => config.products.includes(productSystem));
}

interface ConnectionGroupItem {
    row: ProductRowItem;
    payload: CanvasItemPayload;
    groupSize: number;
    position: number;
    activeProductFormId: number;
    combinationsEnabled: boolean;
}

function getFormDataValue(payload: CanvasItemPayload, pid: string): ConfiguratorValue | undefined {
    return payload.formData.find((field) => field.id === pid)?.value;
}

function isCombinationEnabled(value: ConfiguratorValue | undefined): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
}

function createConnectionGroupItem(
    row: ProductRowItem,
    payload: MultipositionProduct | undefined,
    productSystemName?: string,
): ConnectionGroupItem | null {
    const currentConfig = mapProductTypeToConfiguration(row, productSystemName);

    if (!payload || !currentConfig) {
        return null;
    }

    const canvasPayload = payload as CanvasItemPayload;
    const groupSize = Number(
        getFormDataValue(canvasPayload, getConfigurationPid(currentConfig, 'NUMBER_OF_COMBINATIONS_PID')),
    );
    const position = Number(
        getFormDataValue(canvasPayload, getConfigurationPid(currentConfig, 'ASSEMBLY_POSITION_PID')),
    );
    const combinationsValue = getFormDataValue(canvasPayload, getConfigurationPid(currentConfig, 'COMBINATIONS_PID'));
    console.log('Row:', row);
    console.log('Payload:', payload);
    console.log('Current Config:', currentConfig);
    console.log('Group Size:', groupSize);
    console.log('Position:', position);
    console.log('Combinations Value:', combinationsValue);
    return {
        row,
        payload: canvasPayload,
        groupSize,
        position,
        activeProductFormId: row.activeProductFormId,
        combinationsEnabled: isCombinationEnabled(combinationsValue),
    };
}

function isConnectionGroupStart(item: ConnectionGroupItem): boolean {
    return item.combinationsEnabled && [2, 3].includes(item.groupSize) && item.position === 1;
}

function belongsToConnectionGroup(
    group: ConnectionGroupItem[],
    item: ConnectionGroupItem,
    index: number,
    previousIndex: number,
): boolean {
    const firstItem = group[0];
    const nextAssemblyPosition = group.length + 1;
    console.log(index, previousIndex);
    return (
        item.combinationsEnabled &&
        item.groupSize === firstItem.groupSize &&
        item.position === nextAssemblyPosition &&
        index === previousIndex + 1
    );
}

function applyConnection(group: ConnectionGroupItem[], connection: number): void {
    for (const item of group) {
        Vue.set(item.row, 'connection', connection);
        Vue.set(item.payload, 'connection', connection);
    }
}

export function mapConnections(
    itemRows: ProductRowItem[],
    itemPayloads: MultipositionProduct[],
    productSystemName?: string,
): ProductRowItem[] {
    let currentGroup: ConnectionGroupItem[] = [];
    let previousConnectionIndex = -1;

    for (const row of itemRows) {
        if (row.connection != null) {
            syncCombinationConnectionCounter(row.connection);
        }
    }

    for (let index = 0; index < itemRows.length; index++) {
        const item = createConnectionGroupItem(itemRows[index], itemPayloads[index], productSystemName);

        if (!item) {
            currentGroup = [];
            continue;
        }

        if (isConnectionGroupStart(item)) {
            console.log('Starting new connection group with item:', item);
            currentGroup = [item];
            previousConnectionIndex = index;
            continue;
        }

        if (!currentGroup.length || !belongsToConnectionGroup(currentGroup, item, index, previousConnectionIndex)) {
            console.log('Item does not belong to current group, resetting group. Item:', item);
            currentGroup = [];
            continue;
        }

        currentGroup.push(item);
        previousConnectionIndex = index;
        if (currentGroup.length === currentGroup[0].groupSize) {
            console.log('Completed connection group:', currentGroup);
            const connection =
                currentGroup.find((groupItem) => groupItem.row.connection != null)?.row.connection ??
                getNextCombinationConnection();
            applyConnection(currentGroup, connection);
            currentGroup = [];
        }
    }
    console.log('Current Group:', currentGroup);

    return itemRows;
}

function mapItemsPerConnection(
    itemPayloads: MultipositionProduct[],
    itemRows: ProductRowItem[],
    productSystemName?: string,
): MappedConnectionItems {
    const connectionMap: MappedConnectionItems = {};

    itemRows.forEach((row, index) => {
        if (row.connection == null) {
            return;
        }

        const payload = itemPayloads[index];
        const currentConfig = mapProductTypeToConfiguration(row, productSystemName);
        if (!payload || !currentConfig) {
            return;
        }

        if (!connectionMap[row.connection]) {
            connectionMap[row.connection] = [];
        }

        connectionMap[row.connection].push({ payload, row, currentConfig });
    });

    return connectionMap;
}

export async function buildCanvasFromFormData(payload: {
    itemPayloads: MultipositionProduct[];
    itemRows: ProductRowItem[];
    productSystemName?: string;
}): Promise<CombinationCanvasImages> {
    const mappedItemsPerConnection = mapItemsPerConnection(
        payload.itemPayloads,
        payload.itemRows,
        payload.productSystemName,
    );
    console.log('Mapped Items Per Connection:', mappedItemsPerConnection);
    const canvasPayloads = extractAndFormatChecklistValues(mappedItemsPerConnection);
    console.log('Extracted and Formatted Checklist Values:', canvasPayloads);
    return buildCanvasFromChecklistValues(canvasPayloads);
}
